if (!customElements.get('paint-smart-selector')) {
  customElements.define(
    'paint-smart-selector',
    class PaintSmartSelector extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        const dataEl = this.querySelector('[data-paint-selector-data]');
        if (!dataEl) return;

        this.data = JSON.parse(dataEl.textContent);
        this.optionNames = this.data.optionNames || [];
        this.finishOptionIndex = this.findOptionIndex(['afwerking', 'verfafwerking', 'finish']);
        this.amountOptionIndex = this.findOptionIndex(['hoeveelheid', 'inhoud', 'maat', 'volume', 'size']);

        if (this.finishOptionIndex === -1) {
          this.finishOptionIndex = this.inferFinishOptionIndex();
        }

        if (this.amountOptionIndex === -1) {
          this.amountOptionIndex = this.inferAmountOptionIndex();
        }

        if (this.finishOptionIndex === -1 || this.amountOptionIndex === -1) {
          this.hidden = true;
          return;
        }

        this.els = {
          applicationGroup: this.querySelector('[data-paint-application-group]'),
          applications: this.querySelector('[data-paint-applications]'),
          finishGroup: this.querySelector('[data-paint-finish-group]'),
          finishStep: this.querySelector('[data-paint-finish-step]'),
          finishes: this.querySelector('[data-paint-finishes]'),
          finishDetails: this.querySelector('[data-paint-finish-details]'),
          specificationDetails: document.querySelector(`[data-paint-specification-details="${CSS.escape(this.dataset.productFormId)}"]`),
          finishSpecsList: document.querySelector(`[data-paint-finish-specs-list="${CSS.escape(this.dataset.productFormId)}"]`),
          amountGroup: this.querySelector('[data-paint-amount-group]'),
          amountStep: this.querySelector('[data-paint-amount-step]'),
          amounts: this.querySelector('[data-paint-amounts]'),
          sampleGroup: this.querySelector('[data-paint-sample-group]'),
          samples: this.querySelector('[data-paint-samples]'),
          reset: this.querySelector('[data-paint-reset]'),
        };

        this.finishes = [...(this.data.finishes || [])]
          .map((finish) => ({
            ...finish,
            applications: Array.isArray(finish.applications) ? finish.applications.filter(Boolean) : [],
            recommendedApplications: Array.isArray(finish.recommendedApplications)
              ? finish.recommendedApplications.filter(Boolean)
              : [],
          }))
          .sort((a, b) => Number(a.sortOrder || 999) - Number(b.sortOrder || 999));

        this.applications = [...new Set(this.finishes.flatMap((finish) => finish.applications))];

        this.hasRecommendationData = this.finishes.some((finish) => finish.recommendedApplications.length > 0);

        if (this.finishes.length === 0) {
          this.hidden = true;
          return;
        }

        this.hasApplicationFilters = this.applications.length > 0;

        if (!this.hasApplicationFilters) {
          this.applications = ['Alle toepassingen'];
          this.finishes = this.finishes.map((finish) => ({
            ...finish,
            applications: ['Alle toepassingen'],
          }));
          this.classList.add('paint-smart-selector--without-applications');
        }

        const initialFinish = this.getCheckedOptionValue(this.finishOptionIndex);
        const initialAmount = this.getCheckedOptionValue(this.amountOptionIndex);
        const initialFinishMeta = this.finishes.find((finish) => this.isSameValue(finish.name, initialFinish));
        const defaultApplication = initialFinishMeta?.applications?.[0] || this.applications[0] || '';
        const initialApplication = this.hasApplicationFilters && !this.isSampleAmount(initialAmount) && !this.isMobileLayout()
          ? ''
          : defaultApplication;

        this.state = {
          application: initialApplication,
          finish: initialApplication || this.isSampleAmount(initialAmount) ? initialFinish : '',
          amount: initialApplication || this.isSampleAmount(initialAmount) ? initialAmount : '',
          sampleMode: this.isSampleAmount(initialAmount),
        };

        this.productInfo = this.closest('product-info');
        this.applicationInput = this.getApplicationInput();
        this.initialState = {
          application: this.state.application,
          finish: this.state.finish,
          amount: this.state.amount,
          sampleMode: this.state.sampleMode,
        };

        this.els.reset?.addEventListener('click', () => {
          this.state = { ...this.initialState };
          this.render({ syncNative: true });
        });

        this.scheduleMovePurchaseInfo();
        this.updateStepLayout();
        this.render({ syncNative: true });
      }

      get variantSelects() {
        return this.productInfo?.querySelector('variant-selects') || this.closest('.product__block')?.querySelector('variant-selects');
      }

      isMobileLayout() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      normalize(value) {
        return String(value || '')
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]/g, '');
      }

      isSameValue(valueA, valueB) {
        return this.normalize(valueA) === this.normalize(valueB);
      }

      findOptionIndex(optionNames) {
        const normalizedOptionNames = optionNames.map((name) => this.normalize(name));
        return this.optionNames.findIndex((name) => normalizedOptionNames.includes(this.normalize(name)));
      }

      inferFinishOptionIndex() {
        const finishNames = (this.data.finishes || []).map((finish) => this.normalize(finish.name));
        if (finishNames.length === 0) return -1;

        return this.optionNames.findIndex((_, optionIndex) =>
          (this.data.variants || []).some((variant) => finishNames.includes(this.normalize(variant.options?.[optionIndex])))
        );
      }

      inferAmountOptionIndex() {
        const amountPattern = /(\d+(?:[.,]\d+)?)\s*(ml|milliliter|l|liter|ltr)|sample/i;

        return this.optionNames.findIndex((_, optionIndex) => {
          if (optionIndex === this.finishOptionIndex) return false;

          return (this.data.variants || []).some((variant) => amountPattern.test(String(variant.options?.[optionIndex] || '')));
        });
      }

      getApplicationInput() {
        return document.querySelector(`#${this.dataset.productFormId} [data-paint-application-property]`);
      }

      scheduleMovePurchaseInfo(attempt = 0) {
        const moved = this.movePurchaseInfo();
        if (moved || attempt >= 8) return;

        window.setTimeout(() => this.scheduleMovePurchaseInfo(attempt + 1), 50);
      }

      movePurchaseInfo() {
        const target = document.querySelector(`[data-paint-purchase-info-target="${CSS.escape(this.dataset.productFormId)}"]`);
        if (!target || target.dataset.paintPurchaseInfoMoved === 'true') return Boolean(target);

        const sources = ['price', 'inventory', 'shipping']
          .map((source) => this.productInfo?.querySelector(`[data-paint-purchase-info-source="${source}"]`))
          .filter(Boolean);

        if (sources.length === 0) return false;

        sources.forEach((source) => target.appendChild(source));
        target.hidden = false;
        target.dataset.paintPurchaseInfoMoved = 'true';
        return true;
      }

      updateStepLayout() {
        if (this.hasApplicationFilters) return;

        this.els.applicationGroup.hidden = true;

        if (this.els.finishStep) {
          this.els.finishStep.textContent = '1';
        }

        if (this.els.amountStep) {
          this.els.amountStep.textContent = '2';
        }
      }

      getCheckedOptionValue(optionIndex) {
        const fieldset = this.getOptionFieldset(optionIndex);
        const checkedInput = fieldset?.querySelector('input[type="radio"]:checked');
        const select = this.getOptionSelect(optionIndex);
        const selectedOption = select?.selectedOptions?.[0];

        return checkedInput?.value || selectedOption?.value || '';
      }

      getVariantsForFinish(finishName) {
        return (this.data.variants || []).filter((variant) => this.isSameValue(variant.options[this.finishOptionIndex], finishName));
      }

      getAvailableAmountsForFinish(finishName) {
        return [
          ...new Set(
            this.getVariantsForFinish(finishName)
              .filter((variant) => variant.available)
              .map((variant) => variant.options[this.amountOptionIndex])
              .filter(Boolean)
          ),
        ];
      }

      getAmountSortValue(amount) {
        const amountText = String(amount || '').toLowerCase();
        const match = amountText.match(/(\d+(?:[.,]\d+)?)\s*(ml|milliliter|l|liter|ltr)?/i);

        if (!match) return Number.MAX_SAFE_INTEGER;

        const numericValue = Number(match[1].replace(',', '.'));
        if (!Number.isFinite(numericValue)) return Number.MAX_SAFE_INTEGER;

        const unit = match[2] || '';
        return /ml|milliliter/.test(unit) ? numericValue / 1000 : numericValue;
      }

      sortAmounts(amounts) {
        return [...amounts].sort((amountA, amountB) => {
          const sortA = this.getAmountSortValue(amountA);
          const sortB = this.getAmountSortValue(amountB);

          if (sortA !== sortB) return sortA - sortB;

          return String(amountA).localeCompare(String(amountB), 'nl', { numeric: true, sensitivity: 'base' });
        });
      }

      isSampleAmount(amount) {
        return /sample|staal/i.test(String(amount || ''));
      }

      getPaintAmountsForFinish(finishName) {
        return this.sortAmounts(this.getAvailableAmountsForFinish(finishName).filter((amount) => !this.isSampleAmount(amount)));
      }

      getSampleVariantsForApplication(application) {
        const finishes = this.getFinishesForApplication(application);
        const finishNames = finishes.map((finish) => finish.name);

        return (this.data.variants || []).filter(
          (variant) =>
            variant.available &&
            this.isSampleAmount(variant.options?.[this.amountOptionIndex]) &&
            finishNames.some((finishName) => this.isSameValue(finishName, variant.options?.[this.finishOptionIndex]))
        );
      }

      getSampleVariants() {
        return (this.data.variants || []).filter(
          (variant) =>
            variant.available &&
            this.isSampleAmount(variant.options?.[this.amountOptionIndex])
        );
      }

      getFirstSampleVariantForApplication(application) {
        return this.getSampleVariantsForApplication(application)[0] || null;
      }

      getFirstSampleVariant() {
        return this.getSampleVariants()[0] || null;
      }

      getFinishesForApplication(application) {
        if (!application) return [];

        return this.finishes
          .filter((finish) => finish.applications.some((finishApplication) => this.isSameValue(finishApplication, application)))
          .sort((finishA, finishB) => Number(this.isRecommendedFinishForApplication(finishB, application)) - Number(this.isRecommendedFinishForApplication(finishA, application)));
      }

      getFirstAvailableFinish(finishes) {
        return finishes.find((finish) => this.getPaintAmountsForFinish(finish.name).length > 0) || finishes[0] || null;
      }

      normaliseState() {
        if (this.state.sampleMode) {
          const sampleVariant = this.getFirstSampleVariant();

          if (sampleVariant) {
            this.state.finish = sampleVariant.options[this.finishOptionIndex] || '';
            this.state.amount = sampleVariant.options[this.amountOptionIndex] || '';
            return;
          }

          this.state.sampleMode = false;
        }

        if (this.hasApplicationFilters && !this.state.application) {
          this.state.finish = '';
          this.state.amount = '';
          return;
        }

        if (!this.applications.some((application) => this.isSameValue(application, this.state.application))) {
          this.state.application = this.applications[0] || '';
        }

        const finishes = this.getFinishesForApplication(this.state.application);
        const selectedFinish = finishes.find((finish) => this.isSameValue(finish.name, this.state.finish));
        const selectedFinishHasAmount = selectedFinish && this.getPaintAmountsForFinish(selectedFinish.name).length > 0;

        if (!selectedFinish || !selectedFinishHasAmount) {
          this.state.finish = this.getFirstAvailableFinish(finishes)?.name || '';
        } else {
          this.state.finish = selectedFinish.name;
        }

        const amounts = this.state.finish ? this.getPaintAmountsForFinish(this.state.finish) : [];
        const selectedAmount = amounts.find((amount) => this.isSameValue(amount, this.state.amount));
        this.state.amount = selectedAmount || amounts[0] || '';
      }

      createOption(label, pressed, onClick, disabled = false, className = '') {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `paint-smart-selector__option${className ? ` ${className}` : ''}`;
        button.textContent = label;
        button.disabled = disabled;
        button.setAttribute('aria-pressed', pressed ? 'true' : 'false');
        button.addEventListener('click', onClick);
        return button;
      }

      createCheckIcon() {
        const icon = document.createElement('span');
        icon.className = 'paint-smart-selector__check';
        icon.innerHTML = '<svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="m5 12 4 4 10-10" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        return icon;
      }

      getLegacyRecommendedFinishName(applicationValue) {
        const application = this.normalize(applicationValue);

        if (application.includes('binnen') && application.includes('plafond')) {
          return 'Intelligent Matt Emulsion';
        }

        if (application.includes('keuken') && application.includes('badkamer')) {
          return 'Intelligent Eggshell';
        }

        if (application.includes('houtwerk') && (application.includes('radiator') || application.includes('radiotor'))) {
          return 'Intelligent Eggshell';
        }

        if (application.includes('buiten') && application.includes('hout') && application.includes('metaal')) {
          return 'Intelligent Exterior Eggshell';
        }

        return '';
      }

      isRecommendedFinishForApplication(finish, application) {
        if (!application) {
          return false;
        }

        // Data-driven: metaobject field `aanbevolen_toepassingen` on Verfafwerking.
        if (this.hasRecommendationData) {
          return (finish.recommendedApplications || []).some((recommendedApplication) =>
            this.isSameValue(recommendedApplication, application)
          );
        }

        // Legacy fallback for products whose finishes have no recommendation data yet.
        const recommendedFinishName = this.getLegacyRecommendedFinishName(application);

        if (!recommendedFinishName) {
          return false;
        }

        return this.isSameValue(finish.name, recommendedFinishName);
      }

      isRecommendedFinish(finish) {
        return this.isRecommendedFinishForApplication(finish, this.state.application);
      }

      createFinishOption(finish, pressed, onClick, disabled = false) {
        const button = this.createOption('', pressed, onClick, disabled, 'paint-smart-selector__option--finish');
        const check = this.createCheckIcon();
        const content = document.createElement('span');
        const title = document.createElement('span');
        const meta = document.createElement('span');

        content.className = 'paint-smart-selector__finish-content';
        title.className = 'paint-smart-selector__finish-title';
        meta.className = 'paint-smart-selector__finish-meta';

        title.textContent = finish.name;

        meta.textContent = finish.description || finish.sheen || '';

        content.appendChild(title);

        if (meta.textContent) {
          content.appendChild(meta);
        }

        button.appendChild(check);

        if (this.isRecommendedFinish(finish)) {
          const badge = document.createElement('span');
          badge.className = 'paint-smart-selector__recommended';
          badge.textContent = 'Meest gekozen';
          button.appendChild(badge);
        }

        button.appendChild(content);

        return button;
      }

      createAmountOption(amount, pressed, onClick) {
        const button = this.createOption('', pressed, onClick, false, 'paint-smart-selector__option--amount');
        const label = document.createElement('span');
        const price = document.createElement('span');
        const variant = (this.data.variants || []).find(
          (item) =>
            item.available &&
            this.isSameValue(item.options?.[this.finishOptionIndex], this.state.finish) &&
            this.isSameValue(item.options?.[this.amountOptionIndex], amount)
        );

        label.className = 'paint-smart-selector__amount-label';
        price.className = 'paint-smart-selector__amount-price';
        label.textContent = amount;
        price.textContent = variant?.priceFormatted || '';

        button.appendChild(label);
        if (price.textContent) {
          button.appendChild(price);
        }

        return button;
      }

      createSampleOption(sampleAmount, sampleVariant) {
        const button = this.createOption('', this.state.sampleMode, () => {
          this.state.sampleMode = true;
          this.state.finish = sampleVariant.options[this.finishOptionIndex] || '';
          this.state.amount = sampleAmount;
          this.render({ syncNative: true });
        }, false, 'paint-smart-selector__option--sample');

        const icon = document.createElement('span');
        const copy = document.createElement('span');
        const title = document.createElement('span');
        const description = document.createElement('span');

        icon.className = 'paint-smart-selector__sample-icon';
        copy.className = 'paint-smart-selector__sample-copy';
        title.className = 'paint-smart-selector__sample-title';
        description.className = 'paint-smart-selector__sample-description';

        icon.innerHTML = '<svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M4 4h9v14H8a4 4 0 0 1-4-4z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M13 4l6 6L11 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        const sampleFinishValue = sampleVariant.options?.[this.finishOptionIndex] || '';
        const sampleFinish = this.finishes.find((finish) => this.isSameValue(finish.name, sampleFinishValue));
        const sampleFinishLabel = sampleFinish?.name || sampleFinishValue;

        title.textContent = sampleAmount;
        description.textContent = sampleFinishLabel ? `Matte muurverf (${sampleFinishLabel})` : 'Matte muurverf';

        copy.appendChild(title);
        copy.appendChild(description);
        button.appendChild(icon);
        button.appendChild(copy);

        return button;
      }

      createSpecificationRow(label, value) {
        const row = document.createElement('div');
        const labelCell = document.createElement('div');
        const valueCell = document.createElement('div');
        const labelText = document.createElement('span');
        const valueText = document.createElement('span');

        row.className = 'specs-table__row';
        labelCell.className = 'specs-table__cell';
        valueCell.className = 'specs-table__cell';
        labelText.className = 'specs-table__label';
        valueText.className = 'specs-table__value';

        labelText.textContent = label;
        valueText.textContent = value;

        labelCell.appendChild(labelText);
        valueCell.appendChild(valueText);
        row.appendChild(labelCell);
        row.appendChild(valueCell);

        return row;
      }

      getOptionFieldset(optionIndex) {
        return Array.from(this.variantSelects?.querySelectorAll('fieldset') || [])[optionIndex] || null;
      }

      getOptionSelect(optionIndex) {
        const optionName = this.optionNames[optionIndex];
        const selects = Array.from(this.variantSelects?.querySelectorAll('select') || []);

        return (
          selects[optionIndex] ||
          this.variantSelects?.querySelector(`select[name="options[${CSS.escape(optionName)}]"]`) ||
          null
        );
      }

      getOptionControl(optionIndex, value) {
        const fieldset = this.getOptionFieldset(optionIndex);
        const radio = Array.from(fieldset?.querySelectorAll('input[type="radio"]') || []).find((input) =>
          this.isSameValue(input.value, value)
        );

        if (radio) return radio;

        const select = this.getOptionSelect(optionIndex);
        if (!select) return null;

        const option = Array.from(select.options || []).find((selectOption) => this.isSameValue(selectOption.value, value));
        return option ? select : null;
      }

      selectNativeOption(optionIndex, value, dispatch = false) {
        const control = this.getOptionControl(optionIndex, value);
        if (!control) return null;

        if (control.matches?.('input[type="radio"]')) {
          const changed = !control.checked;
          control.checked = true;

          if (dispatch && changed) {
            control.dispatchEvent(new Event('change', { bubbles: true }));
          }

          return control;
        }

        if (control.tagName === 'SELECT') {
          const option = Array.from(control.options || []).find((selectOption) => this.isSameValue(selectOption.value, value));
          const nextValue = option?.value || value;
          const changed = control.value !== nextValue;
          control.value = nextValue;

          if (dispatch && changed) {
            control.dispatchEvent(new Event('change', { bubbles: true }));
          }

          return control;
        }

        return null;
      }

      getSelectedVariant() {
        return (
          (this.data.variants || []).find(
            (variant) =>
              this.isSameValue(variant.options?.[this.finishOptionIndex], this.state.finish) &&
              this.isSameValue(variant.options?.[this.amountOptionIndex], this.state.amount)
          ) || null
        );
      }

      syncVariantInputs() {
        const variant = this.getSelectedVariant();
        if (!variant) return;

        const inputs = new Set();
        const mainForm = document.getElementById(this.dataset.productFormId);
        const stickyForm = document.getElementById('product-form-sticky-atc-bar');

        mainForm?.querySelectorAll('input[name="id"]').forEach((input) => inputs.add(input));
        this.productInfo?.querySelectorAll('input[name="id"]').forEach((input) => inputs.add(input));
        stickyForm?.querySelectorAll('input[name="id"]').forEach((input) => inputs.add(input));

        inputs.forEach((input) => {
          input.value = variant.id;
          input.disabled = !variant.available;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        const stickySelect = document.querySelector('.sticky-atc-bar__variant-select');
        if (stickySelect) {
          const option = Array.from(stickySelect.options || []).find((selectOption) => String(selectOption.value) === String(variant.id));

          if (option && stickySelect.value !== option.value) {
            stickySelect.value = option.value;
            stickySelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }

      syncNativeOptions(dispatch = false) {
        const finishTarget = this.state.finish ? this.selectNativeOption(this.finishOptionIndex, this.state.finish, false) : null;
        const amountTarget = this.state.amount ? this.selectNativeOption(this.amountOptionIndex, this.state.amount, false) : null;
        const dispatchTarget = amountTarget || finishTarget;

        if (dispatch && dispatchTarget) {
          dispatchTarget.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      syncProperty() {
        this.applicationInput = this.getApplicationInput();

        if (this.applicationInput) {
          this.applicationInput.value = this.state.application || '';
        }
      }

      selectApplication(application) {
        this.state.application = application;
        this.state.finish = '';
        this.state.amount = '';
        this.state.sampleMode = false;
        this.render({ syncNative: true });
      }

      renderApplications() {
        this.els.applications.innerHTML = '';

        if (!this.hasApplicationFilters) return;

        const dropdown = document.createElement('select');
        dropdown.className = 'paint-smart-selector__application-select';
        dropdown.setAttribute('aria-label', 'Waar ga je schilderen?');
        dropdown.value = this.state.application;

        this.applications.forEach((application) => {
          const option = document.createElement('option');
          option.value = application;
          option.textContent = application;
          option.selected = this.isSameValue(this.state.application, application);
          dropdown.appendChild(option);
        });

        dropdown.addEventListener('change', (event) => {
          this.selectApplication(event.currentTarget.value);
        });

        this.els.applications.appendChild(dropdown);

        this.applications.forEach((application) => {
          this.els.applications.appendChild(
            this.createOption(application, this.isSameValue(this.state.application, application), () => {
              this.selectApplication(application);
            })
          );
        });
      }

      renderFinishes() {
        this.els.finishes.innerHTML = '';
        const detailsTarget = this.els.specificationDetails || this.els.finishDetails;
        this.els.finishDetails.hidden = true;
        this.els.finishDetails.innerHTML = '';

        if (detailsTarget) {
          detailsTarget.hidden = true;
          detailsTarget.innerHTML = '';
        }

        const finishes = this.getFinishesForApplication(this.state.application);
        this.els.finishGroup.hidden = finishes.length === 0;

        finishes.forEach((finish) => {
          const hasAvailableVariant = this.getPaintAmountsForFinish(finish.name).length > 0;
          this.els.finishes.appendChild(
            this.createFinishOption(
              finish,
              !this.state.sampleMode && this.isSameValue(this.state.finish, finish.name),
              () => {
                this.state.finish = finish.name;
                this.state.amount = '';
                this.state.sampleMode = false;
                this.render({ syncNative: true });
              },
              !hasAvailableVariant
            )
          );
        });

        const selectedFinish = finishes.find((finish) => this.isSameValue(finish.name, this.state.finish));

        // De volledige specificaties staan server-side in de pagina (snippet
        // paint-finish-specs), standaard dichtgeklapt. Markeer daarin het gekozen
        // blok, en laat daarnaast de losse specs van de keuze staan - dat blok is
        // wat de bezoeker direct ziet.
        this.highlightFinishSpecs(selectedFinish);

        if (!selectedFinish) return;

        const details = [
          selectedFinish.name ? ['Afwerking', selectedFinish.name] : null,
          selectedFinish.sheen ? ['Glansgraad', selectedFinish.sheen] : null,
          selectedFinish.indoorOutdoor ? ['Gebruik', selectedFinish.indoorOutdoor] : null,
          selectedFinish.coverage ? ['Rendement', selectedFinish.coverage] : null,
          selectedFinish.dryingTime ? ['Droogtijd', selectedFinish.dryingTime] : null,
          selectedFinish.recoatTime ? ['Overschilderbaar', selectedFinish.recoatTime] : null,
        ].filter(Boolean);

        if (details.length > 0) {
          detailsTarget.hidden = false;
          detailsTarget.innerHTML = '';
          details.forEach(([label, value]) => {
            detailsTarget.appendChild(this.createSpecificationRow(label, value));
          });
        }
      }

      highlightFinishSpecs(selectedFinish) {
        const list = this.els.finishSpecsList;
        if (!list) return;

        const items = list.querySelectorAll('[data-paint-finish-specs]');

        items.forEach((item) => {
          const isSelected = selectedFinish
            ? this.isSameValue(item.dataset.paintFinishSpecs, selectedFinish.name)
            : false;

          item.classList.toggle('is-selected', isSelected);

          if (isSelected) {
            item.setAttribute('aria-current', 'true');
          } else {
            item.removeAttribute('aria-current');
          }
        });

        list.classList.toggle('has-selection', Boolean(selectedFinish));
      }

      renderAmounts() {
        this.els.amounts.innerHTML = '';

        if (this.state.sampleMode) {
          this.els.amountGroup.hidden = true;
          return;
        }

        const amounts = this.state.finish ? this.getPaintAmountsForFinish(this.state.finish) : [];
        this.els.amountGroup.hidden = amounts.length === 0;

        amounts.forEach((amount) => {
          this.els.amounts.appendChild(
            this.createAmountOption(amount, !this.state.sampleMode && this.isSameValue(this.state.amount, amount), () => {
              this.state.amount = amount;
              this.state.sampleMode = false;
              this.render({ syncNative: true });
            })
          );
        });
      }

      renderSamples() {
        if (!this.els.sampleGroup || !this.els.samples) return;

        this.els.samples.innerHTML = '';

        const sampleVariant = this.getFirstSampleVariant();
        this.els.sampleGroup.hidden = !sampleVariant;

        if (!sampleVariant) return;

        const sampleAmount = sampleVariant.options[this.amountOptionIndex] || 'Sample';
        this.els.samples.appendChild(this.createSampleOption(sampleAmount, sampleVariant));
      }

      renderReset() {
        if (!this.els.reset) return;

        const hasChanged =
          !this.isSameValue(this.state.application, this.initialState.application) ||
          !this.isSameValue(this.state.finish, this.initialState.finish) ||
          !this.isSameValue(this.state.amount, this.initialState.amount) ||
          this.state.sampleMode !== this.initialState.sampleMode;

        this.els.reset.hidden = !hasChanged;
      }

      render({ syncNative = false } = {}) {
        this.normaliseState();
        this.renderApplications();
        this.renderFinishes();
        this.renderAmounts();
        this.renderSamples();
        this.renderReset();
        this.syncProperty();
        this.syncNativeOptions(syncNative);
        this.syncVariantInputs();
      }
    }
  );
}
